import { useEffect, useRef, useState } from "react";
import { PageShell, PageHeaderRow } from "@/components/common";
import "./ArchitectureTab.css";

let mermaidInitialized = false;
let mermaidModulePromise: Promise<typeof import("mermaid").default> | null =
  null;

const MERMAID_DIAGRAMS: Record<string, string> = {
  howItWorks: `graph LR
    subgraph YourSystems["Your Systems"]
        direction TB
        S1["Commerce"]
        S2["Support"]
        S3["Marketing"]
        S4["Fulfillment"]
        S5["Planning"]
        S6["+ More"]
    end

    subgraph TheMill["The Mill"]
        direction TB
        SYNC["Connects &<br/>Updates"]
        AI["AI Reasons<br/>Across Everything"]
        ACT["Delivers Answers<br/>& Takes Action"]

        SYNC --> AI
        AI --> ACT
    end

    subgraph You["You"]
        direction TB
        WEB["Web"]
        SLACK["Slack"]
        AUTO["Automated<br/>Reports"]
    end

    S1 & S2 & S3 --> SYNC
    S4 & S5 & S6 --> SYNC
    ACT --> WEB
    ACT --> SLACK
    ACT --> AUTO`,

  crossSystem: `sequenceDiagram
    participant U as You
    participant AI as The Mill
    participant D as Your Systems

    U->>AI: "Why are support tickets up this week?"
    AI->>D: Pulls support data
    D-->>AI: Ticket volume + categories
    AI->>D: Pulls recent launches
    D-->>AI: 2 launches this week
    AI->>D: Pulls fulfillment data
    D-->>AI: Shipping delays on 1 SKU
    AI-->>U: "Tickets are up 40% — correlated with the<br/>V-Neck Refresh launch. 68% of new tickets<br/>mention shipping delays on that SKU.<br/>Here's a draft Gorgias macro to handle them."`,

  automations: `graph TB
    subgraph Runs["Runs Automatically"]
        direction LR
        T1["Weekly Business<br/>Digest"]
        T2["Daily Support<br/>Summary"]
        T3["Launch Readiness<br/>Check"]
    end

    subgraph Runs2["  "]
        direction LR
        T4["Anomaly<br/>Alerts"]
        T5["Priority<br/>Tracking"]
        T6["Custom<br/>Reports"]
    end

    subgraph Delivers["Delivered To You"]
        direction LR
        SL["Slack"]
        WB["Web Dashboard"]
    end

    Runs --> Delivers
    Runs2 --> Delivers`,

  security: `flowchart LR
    A["True Classic<br/>API Keys"] --> B["Encrypted &<br/>Isolated"]
    B --> C["Only True Classic<br/>Can Access"]
    C --> D["Your Data"]
    C --> E["Your Credentials"]
    C --> F["Your History"]

    style D fill:#f0f7f0,stroke:#4a9
    style E fill:#f0f7f0,stroke:#4a9
    style F fill:#f0f7f0,stroke:#4a9`,
};

async function loadMermaid() {
  if (!mermaidModulePromise) {
    mermaidModulePromise = import("mermaid").then((module) => module.default);
  }

  const mermaid = await mermaidModulePromise;
  if (!mermaidInitialized) {
    mermaid.initialize({
      startOnLoad: false,
      theme: "base",
      themeVariables: {
        primaryColor: "#f4f5f0",
        primaryTextColor: "#0c0a09",
        primaryBorderColor: "#b09a8e",
        lineColor: "#967969",
        secondaryColor: "#fcfbf7",
        tertiaryColor: "#e0e2d6",
        background: "#fcfbf7",
        mainBkg: "#f4f5f0",
        nodeBorder: "#b09a8e",
        clusterBkg: "#fcfbf7",
        clusterBorder: "#dbdbdb",
        titleColor: "#0c0a09",
        edgeLabelBackground: "#fff",
        fontSize: "13px",
        fontFamily: "var(--font-family-sans)",
      },
      flowchart: { curve: "basis", padding: 16 },
      sequence: { mirrorActors: false, actorFontSize: 13, messageFontSize: 12 },
    });
    mermaidInitialized = true;
  }

  return mermaid;
}

function MermaidDiagram({ id, chart }: { id: string; chart: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [svg, setSvg] = useState<string>("");
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    setSvg("");

    loadMermaid()
      .then((mermaid) => mermaid.render(id, chart))
      .then(({ svg: rendered }) => {
        if (!cancelled) setSvg(rendered);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [id, chart]);

  if (failed) {
    return (
      <div className="mermaid-container mermaid-container--fallback">
        Diagram unavailable
      </div>
    );
  }

  if (!svg) {
    return (
      <div
        className="mermaid-container mermaid-container--loading"
        aria-label="Loading diagram"
      />
    );
  }

  return (
    <div
      ref={ref}
      className="mermaid-container"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

export function ArchitectureTab() {
  return (
    <PageShell>
      <PageHeaderRow
        title="Architecture"
        description="How AI makes your team faster"
      />
      <div className="arch-page">
        <div className="arch-abstract">
          <h2>The short version</h2>
          <p>
            The Mill connects to the systems True Classic already runs
            on&mdash;commerce, support, marketing, fulfillment,
            planning&mdash;and gives every employee an AI that understands the
            full picture. Instead of logging into five tools, pulling reports,
            and manually connecting dots, you ask a question and get an answer
            grounded in real data from across the business.
          </p>
          <p>
            It runs inside your infrastructure, with your API keys, on your
            data. Nothing leaves your environment. And as the business adds new
            tools, The Mill grows with it.
          </p>
        </div>

        <div className="arch-metrics">
          <div className="arch-metric">
            <div className="arch-val">Seconds</div>
            <div className="arch-lbl">To answer cross-system questions</div>
          </div>
          <div className="arch-metric">
            <div className="arch-val">24/7</div>
            <div className="arch-lbl">Automated monitoring</div>
          </div>
          <div className="arch-metric">
            <div className="arch-val">All</div>
            <div className="arch-lbl">Your systems, one AI</div>
          </div>
          <div className="arch-metric">
            <div className="arch-val">Zero</div>
            <div className="arch-lbl">Data shared externally</div>
          </div>
        </div>

        {/* 1. What It Does for You */}
        <section className="arch-section">
          <h2>
            <span className="arch-num">1.</span> What It Does for You
          </h2>
          <p>
            Every team at True Classic uses different tools. Marketing lives in
            Klaviyo. Support lives in Gorgias. Product launches are tracked in
            spreadsheets. Fulfillment data sits in BigQuery. When someone needs
            to understand what&rsquo;s actually happening across the business,
            they spend hours pulling data from each system and stitching it
            together manually.
          </p>
          <p>
            The Mill eliminates that work. It connects to all of your
            systems, keeps the data current, and gives you an AI that can reason
            across everything at once. Ask it a question in plain
            English&mdash;via the web app or Slack&mdash;and it pulls the answer
            from wherever the data lives. No dashboards to build. No reports to
            request. No waiting.
          </p>
          <figure>
            <div className="arch-fig-inner">
              <MermaidDiagram
                id="arch-how"
                chart={MERMAID_DIAGRAMS.howItWorks}
              />
            </div>
            <figcaption>
              <strong>Fig. 1.</strong> The Mill sits between your
              existing systems and your team. You keep using the tools you
              already use&mdash;The Mill just makes them smarter
              together.
            </figcaption>
          </figure>
        </section>

        {/* 2. Cross-System Intelligence */}
        <section className="arch-section">
          <h2>
            <span className="arch-num">2.</span> One Question, Every System
          </h2>
          <p>
            The real power is cross-system reasoning. When you ask &ldquo;why
            are support tickets up this week?&rdquo;&mdash;The Mill
            doesn&rsquo;t just check support data. It pulls in recent product
            launches, checks fulfillment status, looks at marketing campaigns,
            and gives you an answer that connects all the dots. A human doing
            this would need to open four tools and spend 30 minutes. The Mill
            does it in seconds.
          </p>
          <figure>
            <div className="arch-fig-inner">
              <MermaidDiagram
                id="arch-cross"
                chart={MERMAID_DIAGRAMS.crossSystem}
              />
            </div>
            <figcaption>
              <strong>Fig. 2.</strong> A single question triggers lookups across
              support, product launches, and fulfillment&mdash;then synthesizes
              an actionable answer. This is the kind of analysis that used to
              require a meeting.
            </figcaption>
          </figure>
          <p>
            This works for any question that spans systems: &ldquo;Which
            products launching next week have fulfillment risks?&rdquo;
            &ldquo;How did last month&rsquo;s campaigns perform relative to
            support volume?&rdquo; &ldquo;What&rsquo;s the sentiment on our new
            SKUs?&rdquo; The AI figures out which systems to query&mdash;you
            just ask.
          </p>
        </section>

        {/* 3. Automations */}
        <section className="arch-section">
          <h2>
            <span className="arch-num">3.</span> Intelligence on Autopilot
          </h2>
          <p>
            Beyond answering questions, The Mill runs automated reports
            and alerts that keep the team informed without anyone having to ask.
            Weekly business digests, daily support summaries, anomaly detection,
            launch readiness checks&mdash;all configured once and delivered to
            Slack on a schedule.
          </p>
          <p>
            These aren&rsquo;t static reports. Every automation uses the same AI
            reasoning as a live conversation&mdash;pulling from all connected
            systems, identifying patterns, and surfacing what matters. If
            support tickets spike overnight, you know about it before standup.
            If a product launch has a fulfillment risk, the alert goes out
            before it becomes a customer problem.
          </p>
          <figure>
            <div className="arch-fig-inner">
              <MermaidDiagram
                id="arch-auto"
                chart={MERMAID_DIAGRAMS.automations}
              />
            </div>
            <figcaption>
              <strong>Fig. 3.</strong> Automated intelligence delivered to Slack
              and the web dashboard. Configure once, stay informed without
              manual effort.
            </figcaption>
          </figure>
        </section>

        {/* 4. Grows With You */}
        <section className="arch-section">
          <h2>
            <span className="arch-num">4.</span> Grows With the Business
          </h2>
          <p>
            The Mill isn&rsquo;t locked to a fixed set of integrations.
            The systems connected today are the starting point. As True Classic
            adopts new tools or changes vendors, new connections plug into the
            same AI layer. A new integration immediately participates in
            cross-system reasoning and automation&mdash;no rebuild, no
            migration.
          </p>
          <p>
            Teams can also customize how the AI behaves without engineering
            work. Default filters, thresholds, and report parameters are
            adjustable per-team, so marketing and operations can tune The Mill
            to surface what&rsquo;s relevant to them specifically.
          </p>
        </section>

        {/* 5. Your Data, Your Control */}
        <section className="arch-section">
          <h2>
            <span className="arch-num">5.</span> Your Data, Your Control
          </h2>
          <p>
            The Mill runs on True Classic&rsquo;s own API keys and
            credentials. Data stays in your environment. There is no shared data
            layer with other companies, no cross-tenant model training, and no
            vendor lock-in on your operational data. Credentials are encrypted
            and isolated&mdash;only True Classic can access True Classic&rsquo;s
            data.
          </p>
          <figure>
            <div className="arch-fig-inner">
              <MermaidDiagram id="arch-sec" chart={MERMAID_DIAGRAMS.security} />
            </div>
            <figcaption>
              <strong>Fig. 4.</strong> True Classic&rsquo;s API keys, data, and
              conversation history are encrypted and completely isolated.
            </figcaption>
          </figure>
        </section>

        {/* 6. What This Means for Your Team */}
        <section className="arch-section">
          <h2>
            <span className="arch-num">6.</span> What This Means for Your Team
          </h2>
          <figure>
            <div className="arch-fig-inner" style={{ padding: 0 }}>
              <table className="data-table arch-table">
                <thead>
                  <tr>
                    <th>Before The Mill</th>
                    <th>With The Mill</th>
                    <th>Impact</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Open 4 tools to investigate a support spike</td>
                    <td>Ask one question, get a cross-system answer</td>
                    <td>Minutes instead of hours</td>
                  </tr>
                  <tr>
                    <td>Manually build weekly business reviews</td>
                    <td>Automated digest delivered to Slack every Monday</td>
                    <td>Hours of prep time eliminated</td>
                  </tr>
                  <tr>
                    <td>
                      Discover fulfillment issues from customer complaints
                    </td>
                    <td>
                      Anomaly alerts flag problems before customers notice
                    </td>
                    <td>Proactive instead of reactive</td>
                  </tr>
                  <tr>
                    <td>Marketing and support teams operate in silos</td>
                    <td>
                      AI correlates campaigns, tickets, and product data
                      automatically
                    </td>
                    <td>Decisions informed by the full picture</td>
                  </tr>
                  <tr>
                    <td>New tools mean new dashboards and manual data pulls</td>
                    <td>
                      New integrations plug in and immediately work with the AI
                    </td>
                    <td>Scales with the business</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </figure>
        </section>

        <footer className="arch-footer">
          The Mill for True Classic &middot; February 2026
        </footer>
      </div>
    </PageShell>
  );
}
